import { useMemo, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/cn";
import { useCustomizationLocked } from "@/lib/use-customization-locked";
import { usePermissao } from "@/lib/use-permissao";
import { makeComparator, type SortDirection, type SortField } from "../production-sort";
import { NovoEstagioDialog } from "../NovoEstagioDialog";
import { downloadAttachment, useProductionMutations } from "../use-production-mutations";
import { serviceLabel, type IncomingOrderRow, type ServiceRow } from "../production-types";
import { MobilePlanilhaCard } from "./MobilePlanilhaCard";
import { MobilePlanilhaOrderCard } from "./MobilePlanilhaOrderCard";
import { MobileSortSheet } from "./MobileSortSheet";
import { MobileSelecaoBar } from "./MobileSelecaoBar";
import { MobileMoveToStageSheet, type EstagioDestino } from "./MobileMoveToStageSheet";

type Campo = "estagio" | "modelo" | "responsavel";

type Escolha = {
  campo: Campo;
  serviceIds: string[];
  atualId?: string | null;
  subtitulo: string;
  lote: boolean;
};

const TITULO_DO_CAMPO: Record<Campo, string> = {
  estagio: "Alterar estágio",
  modelo: "Status do modelo",
  responsavel: "Alterar responsável",
};

function alternarNoSet(set: Set<string>, id: string): Set<string> {
  const proximo = new Set(set);
  if (proximo.has(id)) proximo.delete(id);
  else proximo.add(id);
  return proximo;
}

/**
 * A Planilha no celular: lista operacional em vez de tabela espremida. Mesmos dados, mesmas
 * mutations e mesma ordenação em três níveis do desktop — muda só a apresentação. A tabela de
 * verdade continua disponível em "Tabela completa", como opção secundária.
 */
export function MobilePlanilhaView({
  rows,
  onVerServico,
  onVerPedido,
  onAbrirTabelaCompleta,
}: {
  rows: ServiceRow[];
  onVerServico?: (row: ServiceRow) => void;
  onVerPedido?: (order: IncomingOrderRow) => void;
  onAbrirTabelaCompleta: () => void;
}) {
  const { utils, setAssignedTo, setModelStage, setProductionStage, stages, modelStages, activeMembers } =
    useProductionMutations();

  const [criterios, setCriterios] = useState<SortField[]>(["none", "none", "none"]);
  const [direcoes, setDirecoes] = useState<SortDirection[]>(["asc", "asc", "asc"]);
  const [folhaOrdenar, setFolhaOrdenar] = useState(false);
  const [agrupar, setAgrupar] = useState(false);
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const [pedidosAbertos, setPedidosAbertos] = useState<Set<string>>(new Set());
  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());
  const [escolha, setEscolha] = useState<Escolha | null>(null);
  const [aplicandoLote, setAplicandoLote] = useState(false);
  /** Serviços que recebem o estágio recém-criado (os mesmos da folha aberta). */
  const [criandoEstagio, setCriandoEstagio] = useState<string[] | null>(null);
  const travado = useCustomizationLocked();
  // Sem `production.manage` a lista vira leitura: nada de estágio, modelo, responsável ou lote.
  const podeGerenciar = usePermissao("production.manage");

  const comparator = useMemo(() => makeComparator(criterios, direcoes), [criterios, direcoes]);
  const semOrdenacao = criterios.every((c) => c === "none");
  const quantosCriterios = criterios.filter((c) => c !== "none").length;

  const linhasOrdenadas = useMemo(
    () => (semOrdenacao ? rows : [...rows].sort(comparator)),
    [rows, semOrdenacao, comparator],
  );

  const grupos = useMemo(() => {
    const mapa = new Map<string, { order: IncomingOrderRow; rows: ServiceRow[] }>();
    for (const row of linhasOrdenadas) {
      if (!mapa.has(row.order.id)) mapa.set(row.order.id, { order: row.order, rows: [] });
      mapa.get(row.order.id)!.rows.push(row);
    }
    return [...mapa.values()];
  }, [linhasOrdenadas]);

  const abrirEscolha = (campo: Campo, row: ServiceRow) => {
    const s = row.service;
    if (!s) return;
    setEscolha({
      campo,
      serviceIds: [s.id],
      atualId: campo === "estagio" ? s.productionStageId : campo === "modelo" ? s.modelStageId : s.assignedToUserId,
      subtitulo: `${serviceLabel(s)} — ${row.order.patientName}`,
      lote: false,
    });
  };

  const abrirEscolhaEmLote = (campo: Campo) =>
    setEscolha({
      campo,
      serviceIds: [...selecionados],
      subtitulo: `${selecionados.size} serviço${selecionados.size > 1 ? "s" : ""} selecionado${selecionados.size > 1 ? "s" : ""}`,
      lote: true,
    });

  const opcoesEscolha: EstagioDestino[] = useMemo(() => {
    if (!escolha) return [];
    if (escolha.campo === "estagio")
      return [
        { id: null, rotulo: "Sem estágio", cor: "#71717a" },
        ...stages.map((s) => ({ id: s.id, rotulo: s.label, cor: s.colorHex })),
      ];
    if (escolha.campo === "modelo")
      return [
        { id: null, rotulo: "Não possui", cor: "#71717a" },
        ...modelStages.map((s) => ({ id: s.id, rotulo: s.label, cor: s.colorHex })),
      ];
    return [
      { id: null, rotulo: "Sem responsável", cor: "#a1a1aa" },
      ...activeMembers.map((m) => ({ id: m.user.id, rotulo: m.user.name ?? m.user.email, cor: "#2457FF" })),
    ];
  }, [escolha, stages, modelStages, activeMembers]);

  const movendo =
    aplicandoLote || setProductionStage.isPending || setModelStage.isPending || setAssignedTo.isPending;

  async function aplicarEscolha(destinoId: string | null) {
    if (!escolha) return;
    const { campo, serviceIds, lote } = escolha;
    try {
      if (lote) setAplicandoLote(true);
      await Promise.all(
        serviceIds.map((serviceId) =>
          campo === "estagio"
            ? setProductionStage.mutateAsync({ serviceId, productionStageId: destinoId })
            : campo === "modelo"
              ? setModelStage.mutateAsync({ serviceId, modelStageId: destinoId })
              : setAssignedTo.mutateAsync({ serviceId, assignedToUserId: destinoId }),
        ),
      );
      if (lote) {
        toast.success(`${serviceIds.length} serviço${serviceIds.length > 1 ? "s" : ""} atualizado${serviceIds.length > 1 ? "s" : ""}.`);
        setSelecionados(new Set());
      }
      setEscolha(null);
    } catch (e: any) {
      toast.error(e.message || "Erro ao atualizar.");
    } finally {
      setAplicandoLote(false);
    }
  }

  const propsDeEdicao = {
    onEditarEstagio: (row: ServiceRow) => abrirEscolha("estagio", row),
    onEditarModelo: (row: ServiceRow) => abrirEscolha("modelo", row),
    onEditarResponsavel: (row: ServiceRow) => abrirEscolha("responsavel", row),
    onBaixarAnexo: (row: ServiceRow) => {
      if (row.service?.attachmentFile) downloadAttachment(utils, row.service.attachmentFile.id);
    },
  };

  return (
    <div data-planilha className={cn(selecionados.size > 0 && "pb-36")}>
      <div className="mb-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setFolhaOrdenar(true)}
          aria-haspopup="dialog"
          className={cn(
            "inline-flex h-11 min-w-0 flex-1 basis-0 items-center justify-center gap-1 rounded-md border px-1.5 text-[clamp(10.5px,3vw,13px)] font-semibold transition-colors",
            quantosCriterios > 0
              ? "border-brand-200 bg-brand-50 text-brand-700 dark:border-primary/30 dark:bg-primary/15 dark:text-primary"
              : "border-zinc-200 bg-white text-zinc-700 dark:border-border dark:bg-card dark:text-foreground",
          )}
        >
          <span className="truncate">Ordenar</span>
          {quantosCriterios > 0 && (
            <span className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-brand-600 px-1 text-[10px] font-bold text-white">
              {quantosCriterios}
            </span>
          )}
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
        </button>

        <button
          type="button"
          onClick={() => setAgrupar((v) => !v)}
          aria-pressed={agrupar}
          aria-label="Agrupar por pedido"
          className={cn(
            "inline-flex h-11 min-w-0 flex-1 basis-0 items-center justify-center gap-1 rounded-md border px-1.5 text-[clamp(10.5px,3vw,13px)] font-semibold transition-colors",
            agrupar
              ? "border-brand-200 bg-brand-50 text-brand-700 dark:border-primary/30 dark:bg-primary/15 dark:text-primary"
              : "border-zinc-200 bg-white text-zinc-700 dark:border-border dark:bg-card dark:text-foreground",
          )}
        >
          {agrupar && <Check className="h-3.5 w-3.5 shrink-0" />}
          <span className="truncate">Por pedido</span>
        </button>

        <button
          type="button"
          onClick={onAbrirTabelaCompleta}
          className="inline-flex h-11 min-w-0 flex-1 basis-0 items-center justify-center rounded-md border border-zinc-200 bg-white px-1 text-[clamp(10.5px,3vw,13px)] font-semibold text-zinc-700 transition-colors dark:border-border dark:bg-card dark:text-foreground"
        >
          <span className="truncate">Tabela completa</span>
        </button>
      </div>

      {agrupar ? (
        <ul className="space-y-2">
          {grupos.map((g) => (
            <MobilePlanilhaOrderCard
              key={g.order.id}
              order={g.order}
              rows={g.rows}
              expandido={pedidosAbertos.has(g.order.id)}
              onAlternarExpansao={() => setPedidosAbertos((prev) => alternarNoSet(prev, g.order.id))}
              selecionados={selecionados}
              onAlternarSelecao={(id) => setSelecionados((prev) => alternarNoSet(prev, id))}
              editavel={podeGerenciar}
              expandidosServicos={expandidos}
              onAlternarExpansaoServico={(id) => setExpandidos((prev) => alternarNoSet(prev, id))}
              onVerServico={onVerServico}
              onVerPedido={onVerPedido}
              {...propsDeEdicao}
            />
          ))}
        </ul>
      ) : (
        <ul className="space-y-2">
          {linhasOrdenadas.map((row) => (
            <MobilePlanilhaCard
              key={row.service ? row.service.id : `${row.order.id}-vazio`}
              row={row}
              selecionado={row.service ? selecionados.has(row.service.id) : false}
              onAlternarSelecao={(id) => setSelecionados((prev) => alternarNoSet(prev, id))}
              expandido={row.service ? expandidos.has(row.service.id) : false}
              onAlternarExpansao={
                row.service ? () => setExpandidos((prev) => alternarNoSet(prev, row.service.id)) : undefined
              }
              onVerServico={onVerServico ? () => onVerServico(row) : undefined}
              onVerPedido={onVerPedido ? () => onVerPedido(row.order) : undefined}
              onEditarEstagio={() => propsDeEdicao.onEditarEstagio(row)}
              onEditarModelo={() => propsDeEdicao.onEditarModelo(row)}
              onEditarResponsavel={() => propsDeEdicao.onEditarResponsavel(row)}
              onBaixarAnexo={() => propsDeEdicao.onBaixarAnexo(row)}
              editavel={podeGerenciar}
            />
          ))}
        </ul>
      )}

      {selecionados.size > 0 && (
        <MobileSelecaoBar
          quantos={selecionados.size}
          aplicando={movendo}
          onEstagio={() => abrirEscolhaEmLote("estagio")}
          onModelo={() => abrirEscolhaEmLote("modelo")}
          onResponsavel={() => abrirEscolhaEmLote("responsavel")}
          onLimpar={() => setSelecionados(new Set())}
        />
      )}

      <MobileSortSheet
        aberto={folhaOrdenar}
        criterios={criterios}
        direcoes={direcoes}
        onFechar={() => setFolhaOrdenar(false)}
        onAplicar={(c, d) => {
          setCriterios(c);
          setDirecoes(d);
        }}
      />

      <MobileMoveToStageSheet
        aberto={escolha !== null}
        titulo={escolha ? TITULO_DO_CAMPO[escolha.campo] : ""}
        paciente={escolha?.subtitulo}
        estagios={opcoesEscolha}
        atualId={escolha?.lote ? undefined : escolha?.atualId}
        movendo={movendo}
        onFechar={() => setEscolha(null)}
        onEscolher={aplicarEscolha}
        onCriarNovo={
          escolha?.campo === "estagio" && !travado ? () => setCriandoEstagio(escolha.serviceIds) : undefined
        }
      />

      <NovoEstagioDialog
        aberto={criandoEstagio !== null}
        onFechar={() => setCriandoEstagio(null)}
        onCriado={(estagio) => {
          const alvos = criandoEstagio ?? [];
          setEscolha(null);
          Promise.all(
            alvos.map((serviceId) =>
              setProductionStage.mutateAsync({ serviceId, productionStageId: estagio.id }),
            ),
          ).catch((e: any) => toast.error(e?.message || "Erro ao aplicar o estágio novo."));
        }}
      />
    </div>
  );
}
